import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { supabase } from "../../lib/supabase";
import { useRealtimeTables } from "../../lib/useRealtimeTable";
import type { Deal } from "../../lib/types";
import DealLink from "../DealLink";
import DealCallText from "../DealCallText";
import PreFindingsCard from "../deal/PreFindingsCard";
import AddOnNotesCard from "../deal/AddOnNotesCard";
import { daysInStage, daysInPhase, daysSinceCreated } from "../../lib/dealAge";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { PAYMENT_PLAN_LABELS, fetchCurrentPlans, usd, type BuildPaymentPlan } from "../../lib/buildPaymentPlan";
import { useAuth } from "../../context/AuthContext";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { approveBuild, canApproveBuilds, sendBuildBack } from "../../lib/buildApproval";
import { homeownerColorsAuthorized } from "../../lib/buildSubmitGates";

/**
 * Build Approvals Tab — the one bottleneck (Oct 22, 2026).
 * Shows deals at `build_submitted`. Every submitted build comes through here
 * and leaves only through build_approve() / build_send_back(); Chad or Robert
 * (BUILD_APPROVER_ROLES) press the buttons, everyone else reads. The tab sits
 * on Chad's, Robert's and the Executive views of the Dispatch board.
 * Checklist: Hover, scope, agreement signed, shingle/drip colors,
 * pre-build payment, homeowner color authorization, trades listed.
 *
 * Files come from `deal_documents` (canonical) with fallback to legacy
 * URL columns on `deals` for older records. Each file chip is clickable —
 * mirrors the JenniferQCView pattern (opens via signed URL on click).
 */

const COLS =
  "id,homeowner_name,homeowner_phone,property_street,property_city,property_state,property_zip,claim_number,stage,phase,owner_id,split_rm_id,shingle_color,drip_edge_color,hover_link,scope_document_url,install_agreement_signed,install_agreement_pdf_url,agreement_status,check_photo_front_url,check_photo_back_url,check_type,trades_to_perform,ho_preferred_build_date,acv_amount,deductible,days_in_stage,stage_entered_at,phase_entered_at,created_at,color_addendum_signed,color_addendum_required,pre_build_amount_received,insurance_company,pre_inspection_data,pre_inspection_findings,pre_inspection_completed,chad_supplement_notes,upgrades,retail_profit,at_cost_retail,upgrades_description,upgrades_internal_note,retail_profit_description,retail_profit_internal_note,at_cost_retail_description,at_cost_retail_internal_note";

interface DocLink {
  deal_id: string;
  file_type: string | null;
  storage_path: string | null;
  file_name: string | null;
  created_at: string | null;
}

type CheckItem = {
  label: string;
  ok: boolean;
  /** Storage-bucket path — opens via signed URL. */
  openPath?: string | null;
  /** Direct external URL — opens in new tab. */
  openHref?: string | null;
  /** Tooltip / file name. */
  fileName?: string | null;
};

function buildChecklist(d: Deal & { __docs?: DocLink[] }): CheckItem[] {
  const docs = d.__docs ?? [];
  const scopeDoc = docs.find((x) => x.file_type === "insurance_scope");
  const agreementDoc = docs.find((x) => x.file_type === "agreement");
  const hoverDoc = docs.find((x) => x.file_type === "hover" || x.file_type === "hover_report");

  // Scope: prefer deal_documents row, fall back to legacy URL column.
  const hasScope = !!scopeDoc?.storage_path || !!d.scope_document_url;
  // Agreement signed: HO e-sign flag OR uploaded signed agreement file
  // (RMs sometimes upload a manually-signed PDF instead of routing through SigningPage).
  const hasAgreement = !!d.install_agreement_signed || !!agreementDoc?.storage_path;
  const agreementPath = agreementDoc?.storage_path ?? null;
  const agreementHref = !agreementPath ? d.install_agreement_pdf_url ?? null : null;

  return [
    {
      label: "Hover uploaded",
      ok: !!hoverDoc?.storage_path || !!d.hover_link,
      openPath: hoverDoc?.storage_path,
      openHref: !hoverDoc?.storage_path ? d.hover_link : null,
      fileName: hoverDoc?.file_name ?? "Hover",
    },
    {
      label: "Insurance scope uploaded",
      ok: hasScope,
      openPath: scopeDoc?.storage_path,
      openHref: !scopeDoc?.storage_path ? d.scope_document_url : null,
      fileName: scopeDoc?.file_name ?? "Insurance Scope",
    },
    {
      label: "Agreement signed",
      ok: hasAgreement,
      openPath: agreementPath,
      openHref: agreementHref,
      fileName: agreementDoc?.file_name ?? "Agreement",
    },
    { label: `Shingle: ${d.shingle_color || "Missing"}`, ok: !!d.shingle_color },
    { label: `Drip edge: ${d.drip_edge_color || "Missing"}`, ok: !!d.drip_edge_color },
    {
      // Numeric column comes back from PostgREST as a string ("0.00"), so
      // !!d.pre_build_amount_received was incorrectly truthy on $0 receipts.
      // Require an actual positive amount. (May 7, 2026)
      label: "Pre-build payment verified",
      ok: Number(d.pre_build_amount_received ?? 0) > 0,
      openPath: null,
      openHref: d.check_photo_front_url ?? d.check_photo_back_url ?? null,
      fileName: "Check photo",
    },
    // Chosen on the signed agreement, or on the signed color addendum (the
    // same rule as the submit gate — src/lib/buildSubmitGates.ts).
    { label: "Colors authorized by the homeowner", ok: homeownerColorsAuthorized(d) },
    { label: "Trades listed", ok: Array.isArray(d.trades_to_perform) && d.trades_to_perform.length > 0 },
  ];
}

function formatAddr(d: Deal) {
  return [d.property_street, [d.property_city, d.property_state].filter(Boolean).join(", "), d.property_zip].filter(Boolean).join(" ");
}

/**
 * Open a deal-documents storage path in a new tab via signed URL.
 * Mobile-safe: opens about:blank synchronously inside the click handler
 * before awaiting (mobile Safari/Chrome block window.open after await).
 */
async function openDocByPath(storagePath: string) {
  const win = window.open("about:blank", "_blank");
  const { data, error } = await supabase.storage
    .from("deal-documents")
    .createSignedUrl(storagePath, 3600);
  if (error || !data?.signedUrl) {
    if (win && !win.closed) win.close();
    alert(`Couldn't open file: ${error?.message ?? "no signed URL"}`);
    return;
  }
  if (win && !win.closed) win.location.href = data.signedUrl;
  else window.location.href = data.signedUrl;
}

function openExternal(href: string) {
  window.open(href, "_blank", "noopener");
}

export default function ChadApprovalsTab() {
  const { role, isSuperUser } = useAuth();
  // Chad or Robert (and leadership) act; everyone else sees the queue read-only.
  const canAct = canApproveBuilds(role, isSuperUser);
  const [deals, setDeals] = useState<Array<Deal & { __docs: DocLink[] }> | null>(null);
  // The first payment plan picked at submission (Oct 21, 2026), by deal.
  const [plans, setPlans] = useState<Record<string, BuildPaymentPlan>>({});
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [approving, setApproving] = useState<Set<string>>(new Set());
  // Supplement notes inline editor — keyed by deal ID
  const [notesOpen, setNotesOpen] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});
  const [notesSaving, setNotesSaving] = useState(false);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  // Refresh on either deal updates or new doc uploads
  useRealtimeTables(["deals", "deal_documents"], reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      const { data, error: err } = await supabase
        .from("deals")
        .select(COLS)
        .eq("stage", "build_submitted")
        .order("stage_entered_at", { ascending: true });
      if (err) { if (!cancelled) setError(err.message); return; }
      const dealRows = (data ?? []) as unknown as Deal[];
      const dealIds = dealRows.map((d) => d.id);

      let docs: DocLink[] = [];
      if (dealIds.length > 0) {
        const { data: docData } = await supabase
          .from("deal_documents")
          .select("deal_id,file_type,storage_path,file_name,created_at")
          .in("deal_id", dealIds)
          .in("file_type", ["insurance_scope", "agreement", "hover", "hover_report", "color_addendum"])
          .order("created_at", { ascending: false });
        docs = (docData ?? []) as DocLink[];
      }
      const enriched = dealRows.map((d) => ({
        ...d,
        __docs: docs.filter((x) => x.deal_id === d.id),
      }));
      const planMap = await fetchCurrentPlans(dealIds).catch((e) => {
        console.warn("[ChadApprovalsTab] payment plans lookup failed:", e);
        return {} as Record<string, BuildPaymentPlan>;
      });
      if (!cancelled) {
        setPlans(planMap);
        setDeals(enriched as Array<Deal & { __docs: DocLink[] }>);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Resolve RM names
  const [members, setMembers] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!deals) return;
    const ids = new Set<string>();
    deals.forEach((d) => { if (d.owner_id) ids.add(d.owner_id); });
    if (ids.size === 0) return;
    supabase
      .from("team_roster")
      .select("id,full_name")
      .in("id", [...ids])
      .then(({ data }) => {
        const map: Record<string, string> = {};
        (data ?? []).forEach((m) => { map[m.id] = m.full_name; });
        setMembers(map);
      });
  }, [deals]);

  async function handleApprove(dealId: string) {
    if (approving.has(dealId)) return;

    const deal = deals?.find((d) => d.id === dealId);
    const checklist = deal ? buildChecklist(deal) : [];
    const missing = checklist.filter((c) => !c.ok);

    if (missing.length > 0) {
      const ok = window.confirm(
        `${missing.length} checklist item${missing.length > 1 ? "s" : ""} incomplete:\n\n` +
        missing.map((c) => `• ${c.label}`).join("\n") +
        "\n\nApprove anyway? (Chad override)"
      );
      if (!ok) return;
    }

    setApproving((prev) => new Set(prev).add(dealId));
    try {
      // One route (Oct 22, 2026): build_approve() is the only way a build
      // leaves build_submitted. It checks the approver, moves the deal to
      // schedule_build, writes the audit row and tells Robert.
      await approveBuild(
        dealId,
        missing.length > 0
          ? `Approved with ${missing.length} checklist item${missing.length > 1 ? "s" : ""} still open: ${missing.map((c) => c.label).join(", ")}`
          : "All checklist items passed",
      );
      reload();
    } catch (err: unknown) {
      alert("Approve failed: " + formatSupabaseError(err));
    } finally {
      setApproving((prev) => { const n = new Set(prev); n.delete(dealId); return n; });
    }
  }

  async function handleSendBack(dealId: string) {
    const reason = prompt("Reason for sending back to the RM (they see it on the deal and on their board):");
    if (!reason || reason.trim().length < 3) return;
    try {
      // build_send_back(): back to install_meeting, the reason on the deal's
      // notes and review notes, the RM and split RM alerted. The signed
      // agreement is left alone.
      await sendBuildBack(dealId, reason.trim());
      reload();
    } catch (err: unknown) {
      alert("Send back failed: " + formatSupabaseError(err));
    }
  }

  async function handleSaveNotes(dealId: string) {
    setNotesSaving(true);
    try {
      const text = notesDraft[dealId] ?? "";
      const { error: err } = await supabase
        .from("deals")
        .update({ chad_supplement_notes: text || null })
        .eq("id", dealId);
      if (err) throw err;
      setNotesOpen(null);
      reload();
    } catch (err: any) {
      alert("Save failed: " + (err.message ?? err));
    } finally {
      setNotesSaving(false);
    }
  }

  if (error)
    return <div className="bg-red-50 text-red-700 p-4 rounded">Couldn't load approvals — {error}</div>;
  if (!deals)
    return <div className="text-slate-500 p-4">Loading build approvals…</div>;

  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">
        Every submitted build comes through here. Chad or Robert checks the list and approves; Robert schedules it after. Nothing else moves a build past this board.
      </p>

      {deals.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-slate-500 text-[12px]">
          No builds pending approval
        </div>
      ) : (
        <div className="space-y-2.5">
          {deals.map((d) => {
            const checklist = buildChecklist(d);
            const allGood = checklist.every((c) => c.ok);
            const rmName = d.owner_id ? members[d.owner_id] ?? "…" : "Unassigned";

            return (
              <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-3.5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-slate-900 text-[13px]">
                    <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-amber-100 text-amber-700">
                    Pending Approval
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mb-2">
                  {isAddressLinkable(d) ? (
                    <a href={mapsHref(d)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline hover:text-slate-700">{formatAddr(d)}</a>
                  ) : (
                    formatAddr(d)
                  )} · RM: {rmName}
                  {d.claim_number && <> · #{d.claim_number}</>}
                </div>

                {/* Checklist grid — clickable when a file is attached */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] mb-2.5">
                  {checklist.map((c) => {
                    const clickable = c.ok && (c.openPath || c.openHref);
                    if (clickable) {
                      return (
                        <button
                          key={c.label}
                          type="button"
                          title={c.fileName ?? c.label}
                          onClick={() => {
                            if (c.openPath) openDocByPath(c.openPath);
                            else if (c.openHref) openExternal(c.openHref);
                          }}
                          className="text-left text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                        >
                          ✅ {c.label}
                        </button>
                      );
                    }
                    return (
                      <div key={c.label} className={c.ok ? "text-slate-700" : "text-slate-500"}>
                        {c.ok ? "✅" : "❌"} {c.label}
                      </div>
                    );
                  })}
                </div>

                {/* First payment plan from the submission — Chad's approval is
                    making sure there is one when the money isn't in yet. */}
                {plans[d.id] && (
                  <div className="mb-2.5 text-[11px] rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-amber-900">
                    💵 {plans[d.id].first_payment_label === "50% deposit" ? "50% deposit" : "First payment"}
                    {plans[d.id].first_payment_due != null && Number(plans[d.id].first_payment_due) > 0 ? ` ${usd(Number(plans[d.id].first_payment_due))}` : ""} not in at submission
                    {" · plan: "}<span className="font-semibold">{PAYMENT_PLAN_LABELS[plans[d.id].plan]}</span>
                    {plans[d.id].note ? ` · "${plans[d.id].note}"` : ""}
                  </div>
                )}

                {/* PRE Findings — Yes-flags surface as amber chips so Chad
                    catches gutter guards / siding rot / satellite before approving. */}
                <PreFindingsCard deal={d} className="mb-2.5" />
                <AddOnNotesCard deal={d} className="mb-2.5" />

                {/* Financials row */}
                <div className="flex gap-3 text-[10px] text-slate-500 mb-2.5 flex-wrap">
                  {d.acv_amount && (
                    <span>ACV: ${Number(d.acv_amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  )}
                  {d.deductible && (
                    <span>Deductible: ${Number(d.deductible).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  )}
                  {d.ho_preferred_build_date && (
                    <span className="text-orange-600 font-semibold">
                      Preferred: {format(new Date(d.ho_preferred_build_date + "T12:00:00"), "MMM d, yyyy")}
                    </span>
                  )}
                  <span>In stage {daysInStage(d)}d · phase {daysInPhase(d) ?? "?"}d · {daysSinceCreated(d) ?? "?"}d since created</span>
                </div>

                {/* Supplement notes — shows current notes + edit button */}
                {d.chad_supplement_notes && notesOpen !== d.id && (
                  <div className="mt-2 text-xs text-slate-700 bg-amber-50 border-l-2 border-bardin-orange rounded pl-2 pr-2 py-1">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Supp notes:</span>{" "}
                    {d.chad_supplement_notes.length > 140 ? d.chad_supplement_notes.slice(0, 140) + "…" : d.chad_supplement_notes}
                  </div>
                )}

                {/* Supplement notes inline editor */}
                {notesOpen === d.id && (
                  <div className="mt-2 space-y-1.5">
                    <textarea
                      rows={4}
                      value={notesDraft[d.id] ?? ""}
                      onChange={(e) => setNotesDraft((prev) => ({ ...prev, [d.id]: e.target.value }))}
                      placeholder="Supplement notes — Apex contact, scope details, outstanding items…"
                      className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-bardin-orange/40 resize-y"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveNotes(d.id)}
                        disabled={notesSaving}
                        className="flex-1 px-2.5 py-1 rounded-md bg-bardin-orange text-white text-[11px] font-semibold hover:bg-bardin-orange/90 transition disabled:opacity-50"
                      >
                        {notesSaving ? "Saving…" : "💾 Save Notes"}
                      </button>
                      <button
                        onClick={() => setNotesOpen(null)}
                        className="px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 text-[11px] hover:bg-slate-50 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {canAct ? (
                    <>
                      <button
                        onClick={() => handleApprove(d.id)}
                        disabled={approving.has(d.id)}
                        className={
                          "px-2.5 py-1 rounded-md text-white text-[11px] font-semibold transition " +
                          (allGood
                            ? "bg-emerald-600 hover:bg-emerald-700"
                            : "bg-emerald-400 hover:bg-emerald-500")
                        }
                      >
                        {approving.has(d.id) ? "Approving…" : "✅ Approve"}
                      </button>
                      <button
                        onClick={() => handleSendBack(d.id)}
                        className="px-2.5 py-1 rounded-md bg-red-50 text-red-600 border border-red-200 text-[11px] font-semibold hover:bg-red-100 transition"
                      >
                        ↩ Send Back
                      </button>
                    </>
                  ) : (
                    <span
                      className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-500 text-[11px] font-semibold"
                      title="Only Chad or Robert (and leadership) approve builds"
                    >
                      Chad or Robert approves from here
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setNotesDraft((prev) => ({ ...prev, [d.id]: (d as any).chad_supplement_notes ?? "" }));
                      setNotesOpen(d.id);
                    }}
                    className={
                      "px-2.5 py-1 rounded-md text-[11px] font-semibold transition border " +
                      ((d as any).chad_supplement_notes
                        ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
                    }
                  >
                    📝 {(d as any).chad_supplement_notes ? "Supp Notes" : "Add Supp Notes"}
                  </button>
                  <DealLink id={d.id}>
                    <span className="px-2.5 py-1 rounded-md bg-white text-slate-600 border border-slate-200 text-[11px] font-semibold hover:bg-slate-50 inline-block cursor-pointer transition">
                      View Deal →
                    </span>
                  </DealLink>
                  <div className="ml-auto">
                    <DealCallText dealId={d.id} homeownerName={d.homeowner_name} homeownerPhone={d.homeowner_phone} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
